'use client';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import type { GeneralLayout } from '@eliadmin/core/src/api/general/types/general-layout-types';
import { Heading } from '../ui/heading';
import { ContactCard } from '../homepage/contact';
import { Button } from '../ui/button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { cn } from '@eliadmin/core/src/utils/style-utils';

type ReservationConfirmationProps = {
  generalLayout: GeneralLayout['data'];
  reservation: {
    reservedAt: string;
    serviceName: string;
    serviceDurationMinutes: number;
  };
  locale?: string;
};

function formatDuration(minutes: number) {
  const m = Number.isFinite(minutes) ? Math.max(0, Math.floor(minutes)) : 0;
  if (m === 0) return '';
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h > 0 && r === 0) return `${h}h`;
  if (h > 0 && r > 0) return `${h}h ${r}min`;
  return `${m}min`;
}

function formatDateTime(value: string, locale?: string) {
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return { date: value, time: '' };
  const resolvedLocale = locale || 'sk-SK';
  const date = new Intl.DateTimeFormat(resolvedLocale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(dt);
  const time = new Intl.DateTimeFormat(resolvedLocale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(dt);
  return { date, time };
}

export function ReservationConfirmation({
  generalLayout,
  reservation,
  locale,
}: ReservationConfirmationProps) {
  const { date, time } = formatDateTime(reservation.reservedAt, locale);
  const duration = formatDuration(reservation.serviceDurationMinutes);

  return (
    <div
      className={cn(
        'custom-container',
        'flex w-full flex-col items-center justify-center gap-8 sm:gap-10 lg:gap-12',
      )}
    >
      <div className="flex w-full flex-col items-center justify-center gap-6">
        <Image
          src={'/images/checked.svg'}
          alt="Rezervácia bola úspešne odoslaná"
          width={48}
          height={48}
          className="object-contain"
        />
        <Heading
          title="Rezervácia bola úspešne odoslaná"
          className={{ container: 'w-full max-w-[588px] items-center justify-center text-center' }}
        />
        <p className="w-full text-center text-sm font-medium sm:text-base">
          Vaša rezervácia nastane dňa{' '}
          <strong className="font-bold">
            {date}
            {time ? ` o ${time}` : ''}, {reservation.serviceName}
            {duration ? ` (${duration})` : ''}
          </strong>
        </p>
      </div>
      <div className="grid w-full grid-cols-1 gap-2 sm:gap-4 md:grid-cols-2 lg:gap-6">
        <div className="col-span-1 grid w-full grid-cols-1 gap-2 sm:gap-4 lg:gap-6">
          <ContactCard
            label="Kde nás nájdete?"
            linkText="Navigovať"
            url={
              'https://www.google.com/maps/place/Tom%C3%A1%C5%A1ikova+8a,+080+01+Pre%C5%A1ov/@49.0170718,21.2249787,205m/data=!3m2!1e3!4b1!4m6!3m5!1s0x473ef2b911743a0b:0xcdc71582d35c4ee2!8m2!3d49.0170718!4d21.2256238!16s%2Fg%2F11pw3gvxwj?entry=ttu&g_ep=EgoyMDI1MTEyMy4xIKXMDSoASAFQAw%3D%3D'
            }
            value={`${generalLayout.contact?.address},<br>${generalLayout.contact?.postcode} Sídlisko III (OC Centrum)`}
            icon="/images/pin.svg"
          />
          <ContactCard
            label="Otváracie hodiny"
            value={'Po - Pia: 9:00 - 18:00<br/>So - Ne: Zatvorené'}
            icon="/images/clock.svg"
          />
        </div>
        <iframe
          title="Mapa"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2624.0219887873255!2d21.223454215833204!3d49.01721049714237!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x473ef3586aa92bf3%3A0xbb1d74b64cdf4adb!2sKADERN%C3%8DCTVO%20KAKADU%20-%20Detsk%C3%A9%2C%20d%C3%A1mske%20a%20p%C3%A1nske%20kadern%C3%ADctvo!5e0!3m2!1sen!2ssk!4v170XXXXXXXXXXXXXX"
          className="col-span-1 h-[200px] w-full rounded-[24px] sm:h-full"
          style={{ border: 0 }}
        />
      </div>
      <Button asChild variant={'dark'} className="w-full sm:w-fit">
        <Link prefetch={false} href={ROUTES.HOME()}>
          Domovská stránka
        </Link>
      </Button>
    </div>
  );
}
